import { createAsyncThunk, createEntityAdapter, createSlice, EntityState } from '@reduxjs/toolkit'
import { RootState, store } from './store'

const sliceName = 'user'

export type UserVideoType = {
  id: number
  title: string
  content: string
  belt: string
  category: string
  challenge: string
  label: string
  vimeoID: string
  status: string
  timestamp: number
  timestampUpload: number
  timestampPendingReview?: number
  timestampReviewed?: number
  thumbnail: string
  rating: number
}

export type LiveVideoType = {
  belt: string
  category: string
  challenge: string
  content: string
  id: number
  status: string
  thumbnail: string
  timestamp: number
  timestampUpload: number
  title: string
  vimeoID: string
}

type FetchUserVideoRequestType = {
  beltID: number
  challengeID: number
  studentID?: string
  force?: boolean
}

export type FetchUserVideosResponseType = {
  videos: Record<string, UserVideoType>
  live_videos: Array<LiveVideoType>
  canUpload: boolean
  showUpload: boolean
  error: string | null
  version: string

  has_uploaded_all_previous_challenges: boolean
  is_current_challenge_uploaded: boolean
  is_current_challenge_reviewed: boolean
  is_reupload_restricted: boolean
  is_restricted_by_time_limit: boolean
}

export type VideoEntityType = {
  data?: FetchUserVideosResponseType
} & FetchUserVideoRequestType

export const getVideoEntityID = (model: FetchUserVideoRequestType) => {
  return `${model.studentID ? model.studentID : ''}_${model.challengeID}`
}

export const videoEntityAdapter = createEntityAdapter<VideoEntityType>({
  selectId: (model) => {
    return getVideoEntityID(model)
  }
})

type RatingByCatType = {
  hasPendingReview: boolean
  hasPendingUpload: boolean
  hasReviewed: boolean
  rating?: {
    points: number
    stars: number
  }
}

export type RatingByBeltType = {
  is_passing_percentage: boolean
  is_passing_points: boolean
  is_passing_minimum_stars: boolean
  percentage_complete: number
  stars: number
  num_valid_challenges: number
  max_possible_points: number
  passing_percentage: number
}

export type UserProgressType = {
  result: {
    can_upgrade: boolean
    current_rank: number
    current_belt_name: string
  }
}

export type AttemptUpgradeType = {
  can_upgrade: boolean
  current_rank: number
  current_belt_name: string
  target_rank: number
  target_belt_name: string
  progress: Record<
    string,
    {
      is_passing_percentage: boolean
      is_passing_points: boolean
      is_passing_minimum_stars: boolean
      percentage_complete: number
      stars: number
      num_valid_challenges: number
      max_possible_points: number
      passing_percentage: number
      user_challenges: Record<
        string,
        {
          total_points: number
          passing_grade: boolean
        }
      >
      user_tutorials: Record<
        string,
        {
          id: string
          user_id: string
          challenge_id: string
          timestamp: string
          status: string
        }
      >
      failed: []
    }
  >
  upgrade_rank_to: number
}

type SliceStateType = {
  fetching: boolean
  fetchingUserRatings: boolean
  videos: EntityState<VideoEntityType>
  ratingByCategory: Record<string, RatingByCatType>
  ratingByBelt: Record<string, RatingByBeltType>
  fetchedKeys: Array<string>
  userProgress: Record<string, UserProgressType>
}

const initialState: SliceStateType = {
  fetching: false,
  fetchingUserRatings: false,
  videos: videoEntityAdapter.getInitialState(),
  ratingByCategory: {},
  ratingByBelt: {},
  fetchedKeys: [],
  userProgress: {}
}

export const videoSelector = (obj: FetchUserVideoRequestType): VideoEntityType | undefined => {
  return videoEntityAdapter.getSelectors().selectById(store.getState().user.videos, getVideoEntityID(obj))
}

export const fetchUserVideos = createAsyncThunk<FetchUserVideosResponseType, FetchUserVideoRequestType>(
  `${sliceName}/fetchUserVideo`,
  async (args, thunkAPI) => {
    const state = (thunkAPI.getState() as RootState).config

    try {
      const url = args.studentID
        ? `${state.setting.root}user/${args.studentID || ''}/challenge/${args.challengeID}/videos`
        : `${state.setting.root}user/challenge/${args.challengeID}/videos`

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': state.setting.nonce
        }
      })
        .then((resp) => resp.json())
        .then((resp) => {
          const userVideos = resp.videos.reduce((accumulator, obj) => {
            accumulator[obj.id] = obj
            return accumulator
          }, {})

          // const liveVideos = resp.live_videos.reduce((accumulator, obj) => {
          //   accumulator[obj.id] = obj
          //   return accumulator
          // }, {})

          return {
            videos: userVideos,
            live_videos: resp.live_videos,
            canUpload: resp.canUpload,
            showUpload: resp.showUpload,
            error: resp.error,
            version: resp.version,
            has_uploaded_all_previous_challenges: resp.has_uploaded_all_previous_challenges,
            is_current_challenge_reviewed: resp.is_current_challenge_reviewed,
            is_current_challenge_uploaded: resp.is_current_challenge_uploaded,
            is_reupload_restricted: resp.is_reupload_restricted,
            is_restricted_by_time_limit: resp.is_restricted_by_time_limit
          } as FetchUserVideosResponseType
        })

      thunkAPI.dispatch(
        fetchUserRatings({
          beltID: args.beltID,
          studentID: args.studentID
        })
      )

      return response
    } catch (e) {
      return thunkAPI.rejectWithValue({})
    }
  },
  {
    condition: (args, thunkAPI) => {
      const videos = (thunkAPI.getState() as RootState).user.videos

      if (!args.force && videos.ids.includes(getVideoEntityID(args))) {
        return false
      }

      return true
    }
  }
)

type FetchUserRatingsResponseType = {
  errors: Array<string>
  result: boolean
  challenge: Record<string, RatingByCatType>
  belt: Record<string, RatingByBeltType>
}

type FetchUserRatingsRequestType = {
  studentID?: string
  beltID: number
  signal?: AbortSignal
}

export const fetchUserRatings = createAsyncThunk<FetchUserRatingsResponseType, FetchUserRatingsRequestType>(
  `${sliceName}/fetchUserRatings`,
  async (args, thunkAPI) => {
    const state = (thunkAPI.getState() as RootState).config

    try {
      const url = args.studentID
        ? `${state.setting.root}user/${args.studentID}/belt/${args.beltID}/ratings`
        : `${state.setting.root}user/belt/${args.beltID}/ratings`

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': state.setting.nonce
        },
        signal: args.signal
      })
        .then((res) => res.json())
        .then((json) => {
          return json
        })

      return response
    } catch (e) {
      return thunkAPI.rejectWithValue({})
    }
  },
  {
    condition: (args, thunkAPI) => {
      const state = (thunkAPI.getState() as RootState)[sliceName]

      if (state.fetchingUserRatings) {
        return false
      }
      // else if (state.fetchedKeys.includes(`${args.beltID}_${args.studentID}`)) {
      //   return false
      // }

      return true
    }
  }
)

export const fetchGetUserCanUpgrade = createAsyncThunk<UserProgressType, { student_id: string }>(
  `${sliceName}/fetchGetUserCanUpgrade`,
  async (args, thunkAPI) => {
    const state = (thunkAPI.getState() as RootState).config

    try {
      const response = await fetch(`${state.setting.root}canUserUpgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': state.setting.nonce
        },
        body: JSON.stringify(args)
      })
        .then((res) => res.json())
        .then((json) => {
          return json
        })

      return response
    } catch (e) {
      return thunkAPI.rejectWithValue({})
    }
  }
)

export const attemptUpdateUserRank = createAsyncThunk<AttemptUpgradeType, { student_id: string }>(
  `${sliceName}/attemptUpdateUserRank`,
  async (args, thunkAPI) => {
    const state = (thunkAPI.getState() as RootState).config

    try {
      const response = await fetch(`${state.setting.root}attemptUpdateUserRank`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': state.setting.nonce
        },
        body: JSON.stringify(args)
      })
        .then((res) => res.json())
        .then((json) => {
          return json
        })

      return response
    } catch (e) {
      return thunkAPI.rejectWithValue({})
    }
  }
)

type DeleteVideoRequestType = {
  vimeoID: number
  beltID: number
  challengeID: number
}

export type DeleteVideoResponseType = {
  author: string
  delete_vimeo_video: boolean
  error: Array<string>
  id: number
  post: {
    ID: number
    comment_count: string
    comment_status: string
    filter: string
    guid: string
    menu_order: number
    ping_status: string
    pinged: string
    post_author: string
    post_content: string
    post_content_filtered: string
    post_date: string
    post_date_gmt: string
    post_excerpt: string
    post_mime_type: string
    post_modified: string
    post_modified_gmt: string
    post_name: string
    post_parent: number
    post_password: string
    post_status: string
    post_title: string
    post_type: string
    to_ping: string
  }
  postid: number
  result: boolean
  vimeo_id: string
}

export const deleteVideo = createAsyncThunk<DeleteVideoResponseType, DeleteVideoRequestType>(
  `${sliceName}/deleteVideo`,
  async (args, thunkAPI) => {
    const state = (thunkAPI.getState() as RootState).config

    try {
      const response = await fetch(`${state.setting.root}video/${args.vimeoID}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': state.setting.nonce
        },
        body: JSON.stringify({
          action: 'delete'
        })
      })
        .then((resp) => resp.json())
        .then((resp) => {
          return resp as DeleteVideoResponseType
        })

      thunkAPI.dispatch(
        fetchUserVideos({
          beltID: args.beltID,
          challengeID: args.challengeID,
          studentID: state.view_student?.studentID,
          force: true
        })
      )

      return response
    } catch (e) {
      thunkAPI.rejectWithValue({})
    }
  }
)

export const userSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    resetUser: () => {
      return initialState
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserVideos.pending, (state) => {
      state.fetching = true
    })

    builder.addCase(fetchUserVideos.fulfilled, (state, action) => {
      state.fetching = false

      videoEntityAdapter.upsertOne(state.videos, {
        beltID: action.meta.arg.beltID,
        challengeID: action.meta.arg.challengeID,
        studentID: action.meta.arg.studentID,
        data: action.payload
      })
    })

    builder.addCase(fetchUserRatings.pending, (state, action) => {
      state.fetchedKeys.push(`${action.meta.arg.beltID}_${action.meta.arg.studentID}`)
      state.fetchingUserRatings = true
    })

    builder.addCase(fetchUserRatings.fulfilled, (state, action) => {
      state.fetchingUserRatings = false
      state.ratingByCategory = {
        ...state.ratingByCategory,
        ...action.payload.challenge
      }
      state.ratingByBelt = {
        ...state.ratingByBelt,
        ...action.payload.belt
      }
    })

    builder.addCase('config/setViewStudent', () => {
      return initialState
    })

    builder.addCase(fetchGetUserCanUpgrade.fulfilled, (state, action) => {
      state.userProgress[action.meta.arg.student_id] = action.payload
    })
  }
})

export const { resetUser } = userSlice.actions
